import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {
    border,
    font,
    semanticColor,
    sizing,
} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import {useCoeditingState} from "../../use-coediting-state.ts";
import {useCoeditingUI} from "../../use-coediting-ui.ts";
import {isEmptyObject, isNotPlaceholder, isPlaceholder} from "../../utils.ts";
import DisclaimerBanner from "../disclaimer-banner.tsx";
import PlaceholderBlock from "../placeholder-block.tsx";

import AnswerKey from "./answer-key.tsx";
import HorizontalRulePageBreak from "./horizontal-rule-page-break.tsx";
import QuestionNode from "./question-node.tsx";
import type {QuizData} from "./types.ts";

const StyledArticle = addStyle("article");
const StyledH1 = addStyle("h1");
const StyledOl = addStyle("ol");

export default function QuizRenderer() {
    const {setContainer} = useCoeditingUI();

    const {data, lastError} = useCoeditingState();

    if (lastError) {
        // TODO(reid): Show a better error state?
        return null;
    }

    const noDataYet = !data || isEmptyObject(data);

    const root = data as unknown as QuizData;

    return (
        <StyledArticle
            style={styles.doc}
            role="tree"
            ref={setContainer}
            tabIndex={0}
            aria-label={t`Editable quiz. Use arrow keys to navigate. Press enter to edit.`}
        >
            {noDataYet && (
                <CircularSpinner style={styles.loadingSpinner} size="medium" />
            )}

            <StyledH1 style={styles.h1}>
                {isPlaceholder(root.title) ? (
                    <PlaceholderBlock
                        placeholderKey={root.title.$placeholder}
                        shape="heading"
                    />
                ) : (
                    root.title
                )}
            </StyledH1>

            {root.disclaimer && <DisclaimerBanner text={root.disclaimer} />}

            {root.contentItem?.contentUrl && (
                <LabelMedium>
                    <Trans>
                        This quiz is based on this{" "}
                        <Link
                            href={root.contentItem.contentUrl}
                            target="_blank"
                        >
                            Khan Academy content
                        </Link>
                        .
                    </Trans>
                </LabelMedium>
            )}

            {isPlaceholder(root.questions) ? (
                <PlaceholderBlock
                    placeholderKey={root.questions.$placeholder}
                />
            ) : (
                <StyledOl style={styles.questionList}>
                    {root?.questions?.map((question, questionIndex) => {
                        const questionPath = ["questions", `${questionIndex}`];

                        return (
                            <QuestionNode
                                key={questionIndex}
                                question={question}
                                path={questionPath}
                            />
                        );
                    })}
                </StyledOl>
            )}

            {isNotPlaceholder(root.questions) && (
                <>
                    <HorizontalRulePageBreak />
                    <AnswerKey questions={root.questions} />
                </>
            )}
        </StyledArticle>
    );
}

const styles = StyleSheet.create({
    doc: {
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        width: "100%",
        maxWidth: 800,
        margin: "0 auto",
        padding: sizing.size_480,
        gap: sizing.size_200,
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: border.radius.radius_040,
        backgroundColor: semanticColor.surface.primary,
    },
    h1: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: font.size.xxLarge,
        fontWeight: 600,
        margin: 0,
    },
    questionList: {
        listStyleType: "digit",
        paddingInlineStart: sizing.size_200,
        fontSize: font.size.medium,
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_200,
    },
    loadingSpinner: {
        alignSelf: "center",
        marginBlockStart: sizing.size_640,
        marginBlockEnd: sizing.size_640,
    },
});
